import * as React from 'react';
import { myCameraStream } from 'state/video';
import { classNames } from 'utils/ui/css';
import log from 'utils/log';
import { Stream } from 'utils/ui/media';
export interface IVideoProps {
  stream: Stream;
  // Stream control props
  autoStart?: boolean;
  audioMuted?: boolean;

  // Styling props
  className?: string;
  videoClassName?: string;

  // Event handlers
  onStreamStart?: (stream: Stream) => void;
  onStreamError?: (error: Error) => void;
  onStreamStop?: () => void;
}

export const Video: React.FC<IVideoProps> = ({
  stream,
  autoStart = true,
  audioMuted = false,
  className,
  videoClassName,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const isMyOwnVideo = stream === myCameraStream().stream;
  // AVS-4302 takeover reliability (Safari): autoplay of UNMUTED MediaStream video is rejected
  // when playback starts long after the triggering gesture (takeover negotiation takes seconds).
  // On NotAllowedError we force-mute so frames still render; the next user gesture lifts it.
  const [forcedMute, setForcedMute] = React.useState(false);
  const baseMuted = isMyOwnVideo || audioMuted;

  // The autoPlay attribute alone is not enough: Safari does not reliably start playback for a
  // srcObject assigned after mount. play() is the source of truth; its rejections drive the
  // fallback ladder. AbortError means the source was swapped mid-play (ontrack rebuilds the
  // MediaStream per track, so swaps come in bursts) — the next attempt supersedes it.
  const attemptPlay = React.useCallback(() => {
    const video = videoRef.current;
    if (!video || (!video.srcObject && !video.src)) return;
    video.play().catch((error: unknown) => {
      const name = (error as { name?: string } | null)?.name;
      if (name === 'AbortError') return;
      if (name === 'NotAllowedError') {
        setForcedMute(true);
        return;
      }
      log.warn('[Video] play() failed', name, error);
    });
  }, []);

  const assignVideoSource = React.useCallback((): void => {
    if (videoRef.current && stream) {
      if (typeof stream === 'string') {
        videoRef.current.src = stream;
      } else if (stream instanceof MediaStream) {
        videoRef.current.srcObject = stream;
      }
      attemptPlay();
    } else if (videoRef.current && !stream) {
      // turn off video stream if no stream is provided (e.g. when video is toggled off)
      videoRef.current.srcObject = null;
    }
  }, [stream, videoRef, attemptPlay]);

  React.useEffect(() => {
    if (autoStart) {
      assignVideoSource();
    }
  }, [autoStart, assignVideoSource]);

  // Forced-mute recovery: retry muted (allowed everywhere, frames render), then lift the mute on
  // the NEXT user gesture. The unmute happens imperatively INSIDE the gesture task — WebKit only
  // honours it while the gesture is current — and the state update makes the prop match it.
  React.useEffect(() => {
    if (!forcedMute) return undefined;
    attemptPlay();
    const restore = () => {
      const video = videoRef.current;
      if (video) {
        video.muted = baseMuted;
        attemptPlay();
      }
      setForcedMute(false);
    };
    document.addEventListener('pointerdown', restore, { once: true, capture: true });
    document.addEventListener('keydown', restore, { once: true, capture: true });
    return () => {
      document.removeEventListener('pointerdown', restore, { capture: true });
      document.removeEventListener('keydown', restore, { capture: true });
    };
  }, [forcedMute, baseMuted, attemptPlay]);

  // Safari can silently pause a live element (tab visibility flips, re-parenting under a
  // crossfade). There is no user pause UI (controls={false}), so a pause with a live source is
  // always spurious — re-kick when visible. Teardown is safe: cleanup nulls srcObject first.
  const handlePause = React.useCallback(() => {
    const video = videoRef.current;
    if (!video || (!video.srcObject && !video.src) || video.ended) return;
    if (document.visibilityState === 'hidden') return;
    attemptPlay();
  }, [attemptPlay]);

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') attemptPlay();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [attemptPlay]);

  // Sever the element from the stream on unmount: React does not pause() a removed media
  // element, and a detached <video> keeps playing audio until it is garbage-collected.
  React.useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video) {
        video.srcObject = null;
      }
    };
  }, []);

  return (
    <>
      <div className={classNames('ex3d-video', className)} part="video-container">
        <video
          ref={videoRef}
          className={classNames(videoClassName, '-scale-x-100')}
          muted={baseMuted || forcedMute}
          playsInline
          autoPlay
          controls={false}
          onLoadedMetadata={attemptPlay}
          onPause={handlePause}
        />
      </div>
    </>
  );
};
